import { RawAxiosResponseHeaders } from 'axios';
import dayjs from 'dayjs';

import Webhook from '~interfaces/webhook';
import WebhookCertificate from '~interfaces/webhookCertificate';
import SkopeiConnectService from '~services/skopeiConnectService';
import { formatBytes } from '~utils/numberUtils';

class WebhooksService extends SkopeiConnectService {
	protected readonly path = 'webhook';

	async getRentalWebhook(signal?: AbortSignal): Promise<Webhook> {
		const endpoint = this.createEndpoint('v2', ['rental'], true);
		return await this.client
			.get<WebhookResponse>(endpoint, {
				signal: signal,
			})
			.then(({ data }) => {
				return Promise.resolve(this.fromResponse(data));
			});
	}

	public getRentalWebhookCacheKey() {
		return [this.path, 'rental'] as const;
	}

	async getSkopeiConnectPublicKeyCertificate(signal?: AbortSignal): Promise<WebhookCertificate> {
		const endpoint = this.createEndpoint('v2', ['public-key'], true);
		return await this.client
			.get<Blob>(endpoint, {
				responseType: 'blob',
				signal: signal,
			})
			.then(({ data, headers }) => {
				return Promise.resolve(this.fromCertificateResponse(headers, data));
			});
	}

	async getSkopeiConnectPublicKeyCertificateHead(
		signal?: AbortSignal
	): Promise<WebhookCertificate> {
		const endpoint = this.createEndpoint('v2', ['public-key'], true);
		return await this.client
			.head<undefined>(endpoint, {
				signal: signal,
			})
			.then(({ headers }) => {
				return Promise.resolve(this.fromCertificateResponse(headers));
			});
	}

	public getSkopeiConnectPublicKeyCertificateCacheKey() {
		return [this.path, 'public-key'] as const;
	}

	async updateRentalWebhook(
		params: {
			clientId: string;
			scope: string;
			tokenEndpoint: string;
			webhookEndpoint: string;
		},
		signal?: AbortSignal
	): Promise<void> {
		const endpoint = this.createEndpoint('v2', ['rental'], true);
		return await this.client
			.put(
				endpoint,
				{
					clientId: params.clientId,
					scope: params.scope,
					tokenEndpoint: params.tokenEndpoint,
					webhookEndpoint: params.webhookEndpoint,
				},
				{
					signal: signal,
				}
			)
			.then(() => {
				return Promise.resolve();
			});
	}

	/**
	 * Model parsing.
	 */
	private fromResponse(response: WebhookResponse): Webhook {
		const { clientId, webHookEndpoint, ...rest } = response;

		return {
			...rest,
			id: crypto.randomUUID().toString(),
			clientId: clientId?.toString(),
			webhookEndpoint: webHookEndpoint,
		};
	}

	private fromCertificateResponse(
		headers: RawAxiosResponseHeaders,
		response?: Blob
	): WebhookCertificate {
		const expiryDateString = headers['x-certificate-expiration']?.toString();
		const contentDisposition = headers['content-disposition']?.toString().replaceAll('"', '');
		const contentLength = (headers['content-length'] as number | undefined) ?? 0;
		const fileName =
			contentDisposition
				?.split('; ')
				?.find((value) => value.includes('filename='))
				?.split('=')[1] ?? 'MissingFileName';
		return {
			file: response,
			fileName: fileName,
			fileSize: contentLength,
			readableFileSize: formatBytes(contentLength),
			expiryDate: expiryDateString ? dayjs(expiryDateString) : dayjs(),
			thumbprint: (headers['x-certificate-thumbprint'] as string | undefined) ?? '',
		};
	}
}

interface WebhookResponse {
	clientId?: number | null;
	scope?: string;
	tokenEndpoint?: string;
	webHookEndpoint?: string;
}

export default WebhooksService;
